import { lazy, Suspense, useEffect, useEffectEvent, useRef, useState } from "react";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { paymentApiService as api } from "../services/paymentApiService";
import {
  formatPaymentMoney, fromPaymentDetailsDtoToPaymentDetails, fromPaymentDtoToPayment,
  fromPaymentPreparationDtoToPaymentPreparation, isPaymentTerminal,
  type Payment, type PaymentDetails, type PaymentPreparation,
} from "../types/paymentTypes";
import { PaymentState } from "./PaymentState";

const StripePayment = lazy(() => import("./StripePayment"));
const STATUS_POLL_MS = 3000;
const MAX_STATUS_POLLS = 10;

export function CustomerPaymentFlow({ token, onDone }: { token: string; onDone?: () => void }) {
  const [details, setDetails] = useState<PaymentDetails>();
  const [payment, setPayment] = useState<Payment>();
  const [preparation, setPreparation] = useState<PaymentPreparation>();
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [confirmationAttempted, setConfirmationAttempted] = useState(false);
  const controller = useRef<AbortController | null>(null);
  const submitting = useRef(false);
  const checkingRef = useRef(false);
  const [polls, setPolls] = useState(0);
  // Bank verification and processing settle shortly after confirmation; check a bounded number of times.
  const awaitingResult = !!payment && !isPaymentTerminal(payment) && (confirmationAttempted || payment.status === "processing");
  const poll = useEffectEvent(() => { setPolls(count => count + 1); void checkStatus(); });
  useEffect(() => {
    if (!awaitingResult || polls >= MAX_STATUS_POLLS || checking || error) return;
    const timer = window.setTimeout(poll, STATUS_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [awaitingResult, polls, checking, error]);
  useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    api.open(token, current.signal).then(fromPaymentDetailsDtoToPaymentDetails).then(result => {
      if (!current.signal.aborted) { setDetails(result); setPayment(result.payment); }
    }).catch(() => {
      if (!current.signal.aborted) setError("This payment link is unavailable or expired. Ask the business to check payment status before issuing a new link.");
    });
    return () => current.abort();
  }, [token, attempt]);

  async function checkStatus() {
    const signal = controller.current?.signal;
    if (!signal || signal.aborted || !details || checkingRef.current) return;
    checkingRef.current = true;
    setChecking(true);
    setError("");
    try {
      const result = fromPaymentDtoToPayment(await api.status(token, signal));
      if (result.id !== details.payment.id || result.amountMinor !== details.amountMinor || result.currency !== details.currency) {
        throw new Error("The payment context changed.");
      }
      if (!signal.aborted) setPayment(result);
    } catch {
      if (!signal.aborted) setError("Couldn't verify payment status. It may still have completed. Ask the business to check this payment; do not pay again.");
    } finally {
      checkingRef.current = false;
      if (!signal.aborted) setChecking(false);
    }
  }
  async function prepare() {
    const signal = controller.current?.signal;
    if (!signal || signal.aborted || !details || !payment || submitting.current || isPaymentTerminal(payment)) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const result = fromPaymentPreparationDtoToPaymentPreparation(await api.prepare(token, signal));
      if (result.publishableKey !== details.publishableKey || result.stripeAccountId !== details.stripeAccountId) {
        throw new Error("Stripe payment context changed.");
      }
      if (!signal.aborted) { setPreparation(result); setConfirmationAttempted(false); }
    } catch {
      if (!signal.aborted) setError("Couldn't prepare the secure payment form. Refresh this payment's status before trying again.");
    } finally {
      submitting.current = false;
      if (!signal.aborted) setBusy(false);
    }
  }
  if (!details || !payment) return error ? <ErrorState message={error}
    onRetry={() => { setError(""); setAttempt(value => value + 1); }} /> : <LoadingSpinner message="Loading payment..." />;
  const canPrepare = ["requires_payment_method", "requires_confirmation", "requires_action"].includes(payment.status);
  const amountLabel = formatPaymentMoney(details.amountMinor, details.currency);
  return <div className="space-y-4">
    <div className="space-y-1" data-telemetry-mask>
      <p className="font-medium">{details.businessName}</p>
      <p className="text-sm">Appointment payment for {details.customerName}: {amountLabel}</p>
      <p className="text-xs text-muted-foreground">{details.environment === "test" ? "Test mode - use test cards only." : "Live mode - this collects a real payment."}</p>
    </div>
    <PaymentState payment={payment} />
    {!isPaymentTerminal(payment) && <>
      <p className="text-sm text-muted-foreground">The customer must complete payment and any bank verification. A new card is not saved for future use.</p>
      <p className="text-xs text-muted-foreground">Secure link expires {details.expiresAt.toLocaleString()}. Expiry does not cancel an attempted payment.</p>
      {canPrepare && (!preparation || confirmationAttempted) && !error && <Button disabled={busy || checking}
        onClick={() => void prepare()}>{busy ? "Preparing payment..." : confirmationAttempted ? "Reopen this payment" : "Continue to secure payment"}</Button>}
      {preparation && !confirmationAttempted && canPrepare && <Suspense fallback={<LoadingSpinner message="Loading secure payment..." />}>
        <StripePayment key={preparation.clientSecret} preparation={preparation} amountLabel={amountLabel}
          returnUrl={`${window.location.origin}/payments/pay#token=${encodeURIComponent(token)}`}
          onConfirmed={async () => { setConfirmationAttempted(true); setPreparation(undefined); setPolls(0); await checkStatus(); }} />
      </Suspense>}
    </>}
    {error && <ErrorState message={error} />}
    {!isPaymentTerminal(payment) && <Button variant="quiet" disabled={busy || checking} onClick={() => void checkStatus()}>
      {checking ? "Checking payment..." : "Refresh payment status"}
    </Button>}
    {isPaymentTerminal(payment) && (onDone ? <Button onClick={onDone}>Done</Button> : <p className="text-sm">You can close this page.</p>)}
  </div>;
}
